import { getPrimePendingMonthStart, validateEmail } from '@bloomays-lib/utils.shared';
import { format } from 'date-fns/format';
import { getMonth } from 'date-fns/getMonth';
import type { PrimesOutletContext } from '../components/pages/PrimesMonthlyPage';
import {
  CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID,
  type ActiveTam,
  type AnnualTamPrimeAggregate,
  type CLevelPrimesDashboardGridRow,
  type CLevelSynthesisExportFooterRow,
  type CLevelSynthesisRow,
  type CLevelSynthesisViewMode,
  type MonthIndex,
  type MonthlySynthesisCell,
  type MonthlySynthesisCellStatus,
  type PrimesMonthlyNavigationState,
} from '../types/primes';
import {
  buildMonthlyPrimeNavigationTarget,
  mapAnnualTamPrimeToTableRows,
  yearToReferenceDate,
} from './annualPrime.utils';

export type CLevelSynthesisMonthNavigationTarget = {
  pathname: string;
  search: string;
  state?: PrimesMonthlyNavigationState;
};

export const FIRST_MARGIN_YEAR = 2026;

const YEAR_PATTERN = /^\d{4}$/;

type CLevelDetailRedirectToastKey =
  | 'historicalDataUnavailable'
  | 'futureYearUnavailable'
  | 'invalidDetailTamParam'
  | 'invalidDetailYearParam';

type CLevelDetailParseResult =
  | { outcome: 'ok'; primeContext: NonNullable<PrimesOutletContext> }
  | { outcome: 'redirect'; toastKey: CLevelDetailRedirectToastKey };

const MONTH_INDICES: MonthIndex[] = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const emptySynthesisCell = (status: MonthlySynthesisCellStatus): MonthlySynthesisCell => ({
  status,
  totalMarge: null,
  totalPrime: null,
  valueSource: null,
});

const createMonths = (status: MonthlySynthesisCellStatus): Record<MonthIndex, MonthlySynthesisCell> =>
  MONTH_INDICES.reduce<Record<MonthIndex, MonthlySynthesisCell>>((acc, monthIndex) => {
    acc[monthIndex] = emptySynthesisCell(status);
    return acc;
  }, {} as Record<MonthIndex, MonthlySynthesisCell>);

export const createLoadingMonths = (): Record<MonthIndex, MonthlySynthesisCell> => createMonths('loading');

export const createEmptyMonths = (): Record<MonthIndex, MonthlySynthesisCell> => createMonths('empty');

export const createErrorMonths = (): Record<MonthIndex, MonthlySynthesisCell> => createMonths('error');

export const createCLevelSynthesisExportFooterRow = (): CLevelSynthesisExportFooterRow => ({
  rowId: CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID,
  rowKind: 'export-footer',
});

export const isCLevelSynthesisExportFooterRow = (
  row: CLevelPrimesDashboardGridRow,
): row is CLevelSynthesisExportFooterRow => row.rowId === CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID;

export const isYearConsultable = (year: string, referenceDate = new Date()): boolean => {
  const selectedYear = Number(year);
  const currentYear = Number(format(referenceDate, 'yyyy'));
  return selectedYear >= FIRST_MARGIN_YEAR && selectedYear <= currentYear;
};

export const parseCLevelDetailSearchParams = (
  searchParams: URLSearchParams,
  referenceDate = new Date(),
): CLevelDetailParseResult => {
  const tam = searchParams.get('tam')?.trim() ?? '';
  const year = searchParams.get('year')?.trim() ?? '';

  if (!tam || !validateEmail(tam)) {
    return { outcome: 'redirect', toastKey: 'invalidDetailTamParam' };
  }

  if (!year || !YEAR_PATTERN.test(year)) {
    return { outcome: 'redirect', toastKey: 'invalidDetailYearParam' };
  }

  if (!isYearConsultable(year, referenceDate)) {
    const selectedYear = Number(year);
    const currentYear = Number(format(referenceDate, 'yyyy'));
    if (selectedYear < FIRST_MARGIN_YEAR) {
      return { outcome: 'redirect', toastKey: 'historicalDataUnavailable' };
    }
    if (selectedYear > currentYear) {
      return { outcome: 'redirect', toastKey: 'futureYearUnavailable' };
    }
    return { outcome: 'redirect', toastKey: 'invalidDetailYearParam' };
  }

  return {
    outcome: 'ok',
    primeContext: {
      tamEmail: tam,
      referenceDate: yearToReferenceDate(year),
    },
  };
};

export const buildTamDisplayName = (tam: ActiveTam): string => {
  const firstname = tam.firstname.trim();
  const lastname = tam.lastname.trim();
  if (firstname && lastname) {
    return `${firstname} ${lastname}`;
  }
  if (firstname) {
    return firstname;
  }
  if (lastname) {
    return lastname;
  }
  return tam.email.trim();
};

export const buildCLevelSynthesisRowIdentity = (
  tam: ActiveTam,
): Pick<CLevelSynthesisRow, 'rowId' | 'tamEmail' | 'tamDisplayName' | 'tamFirstname' | 'tamLastname' | 'luccaId'> => ({
  rowId: tam.email,
  tamEmail: tam.email,
  tamDisplayName: buildTamDisplayName(tam),
  tamFirstname: tam.firstname.trim(),
  tamLastname: tam.lastname.trim(),
  luccaId: tam.luccaId?.trim() || null,
});

const monthYearToMonthIndex = (monthYear: string): MonthIndex | null => {
  const [monthPart] = monthYear.split('/');
  if (!monthPart || monthPart.length !== 2) {
    return null;
  }
  return monthPart as MonthIndex;
};

const padMonthIndex = (month: number): MonthIndex => String(month).padStart(2, '0') as MonthIndex;

export const getCLevelSynthesisCellDisplayValue = (
  cell: MonthlySynthesisCell,
  viewMode: CLevelSynthesisViewMode,
): number | null => {
  if (cell.status !== 'value') {
    return null;
  }
  return viewMode === 'marge' ? cell.totalMarge : cell.totalPrime;
};

export type CLevelMonthColumnExportDisabledReason = 'loading' | 'error' | 'missingLuccaId' | 'pending' | 'noValidated';

export const getCLevelMonthColumnExportDisabledReason = (
  rows: CLevelSynthesisRow[],
  monthIndex: MonthIndex,
): CLevelMonthColumnExportDisabledReason | null => {
  if (rows.length === 0) {
    return 'noValidated';
  }

  if (rows.some((row) => row.rowStatus === 'loading' || row.months[monthIndex].status === 'loading')) {
    return 'loading';
  }

  if (rows.some((row) => row.rowStatus === 'error' || row.months[monthIndex].status === 'error')) {
    return 'error';
  }

  if (rows.some((row) => row.rowStatus === 'success' && !row.luccaId?.trim())) {
    return 'missingLuccaId';
  }

  if (rows.some((row) => row.months[monthIndex].valueSource === 'pending')) {
    return 'pending';
  }

  if (!rows.some((row) => row.months[monthIndex].valueSource === 'validated')) {
    return 'noValidated';
  }

  return null;
};

export const isCLevelSynthesisMonthCellNavigable = (row: CLevelSynthesisRow, monthIndex: MonthIndex): boolean => {
  const cell = row.months[monthIndex];
  return row.rowStatus !== 'loading' && cell.status !== 'loading' && cell.status !== 'error';
};

export const buildCLevelSynthesisMonthNavigationTarget = (
  row: CLevelSynthesisRow,
  monthIndex: MonthIndex,
  selectedYear: string,
): CLevelSynthesisMonthNavigationTarget => {
  const mmYyyy = `${monthIndex}${selectedYear}`;
  const search = `?${new URLSearchParams({ tam: row.tamEmail.trim() }).toString()}`;

  if (row.annualTamPrime) {
    const monthYear = `${monthIndex}/${selectedYear}`;
    const tableRow = mapAnnualTamPrimeToTableRows(row.annualTamPrime).find(
      (candidate) => candidate.monthYear === monthYear,
    );
    if (tableRow) {
      const target = buildMonthlyPrimeNavigationTarget({
        aggregate: row.annualTamPrime,
        row: tableRow,
        tamEmail: row.tamEmail,
        isTamPreview: true,
      });
      return {
        pathname: target.pathname,
        search: target.search,
        state: target.state,
      };
    }
  }

  return {
    pathname: `/primes/${mmYyyy}`,
    search,
  };
};

export const mapAnnualTamPrimeToMonthlySynthesisCells = (
  aggregate: AnnualTamPrimeAggregate,
  year: string,
  referenceDate = new Date(),
): Record<MonthIndex, MonthlySynthesisCell> => {
  const months = createEmptyMonths();

  aggregate.validatedPrimes.forEach((validatedPrime) => {
    const monthIndex = monthYearToMonthIndex(validatedPrime.monthYear);
    if (!monthIndex) {
      return;
    }
    months[monthIndex] = {
      status: 'value',
      totalMarge: validatedPrime.totalMarge,
      totalPrime: validatedPrime.totalPrime,
      valueSource: 'validated',
    };
  });

  if (aggregate.pendingPrimes !== null && year === format(referenceDate, 'yyyy')) {
    const currentMonthIndex = padMonthIndex(getMonth(getPrimePendingMonthStart(referenceDate)) + 1);
    months[currentMonthIndex] = {
      status: 'value',
      totalMarge: aggregate.pendingPrimes.totalMarge,
      totalPrime: aggregate.pendingPrimes.totalPrime,
      valueSource: 'pending',
    };
  }

  return months;
};
